/**
 * Adapted from Open Science `apps/desktop/src/components/thread/BlockList.tsx`
 * and `atoms.tsx` at e2333ef9bdefb9c5438c54fc78d93fe56ec72ed0 (MIT).
 * The message presentation also ports the visual DOM skeleton from assistant-ui
 * `templates/minimal/components/assistant-ui/thread.tsx` at
 * adcfe01e6d5b7bb8bf28d00de033c150dc76fe53 (MIT).
 *
 * Cadence supplies the authoritative ProjectTurn, public event, and Artifact
 * projections instead of Open Science Session/ThreadBlock or assistant-ui
 * MessagePrimitive entities.
 */
import {
  Box,
  ChartNoAxesCombined,
  Check,
  ChevronRight,
  Copy,
  FileCode2,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  Image as ImageIcon,
  NotebookPen,
  PanelRightOpen,
} from "lucide-react";
import { cjk } from "@streamdown/cjk";
import { math } from "@streamdown/math";
import {
  memo,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
} from "react";
import { Streamdown, type UrlTransform } from "streamdown";
import "katex/dist/katex.min.css";
import { ConversationActivityFeed } from "./ConversationActivityGroup";
import type {
  CadenceAgent,
  CadenceAgentEvent,
  CadenceArtifact,
  CadenceTurn,
} from "./types";

export interface OpenSciencePublicAgentReply {
  id: string;
  agentId: string;
  agentName: string;
  agentRole?: string;
  text: string;
  seq: number;
  tsMs?: number;
  sourceLabel: string;
}

function formatTime(value: string | number) {
  const timestamp = typeof value === "number" ? value : Date.parse(value);
  if (!Number.isFinite(timestamp)) return String(value);
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(timestamp);
}

function formatElapsed(milliseconds: number) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1_000));
  if (seconds < 60) return `${seconds} 秒`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes} 分 ${rest} 秒` : `${minutes} 分`;
}

const agentReplyAccents = [
  "var(--cadence-brand)",
  "var(--cadence-data-blue)",
  "var(--cadence-data-green)",
  "var(--cadence-data-violet)",
  "var(--cadence-data-ochre)",
  "var(--cadence-data-rose)",
] as const;

function agentReplyAccent(agentId?: string) {
  if (!agentId) return "var(--cadence-brand)";
  let hash = 0;
  for (const character of agentId) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }
  return agentReplyAccents[hash % agentReplyAccents.length];
}

function agentReplyInitial(name: string) {
  return Array.from(name.trim())[0] ?? "C";
}

function statusLabel(turn: CadenceTurn) {
  if (turn.submissionUncertain) return "提交待确认";
  return {
    pending: "排队中",
    running: "工作中",
    interrupted_recoverable: "提交状态待核实",
    result_unknown: "结果未知（已抑制自动恢复）",
    completed: "已完成",
    error: "未完成",
    cancelled: "已取消",
  }[turn.status];
}

function copyText(text: string) {
  return navigator.clipboard?.writeText(text) ?? Promise.reject(new Error("Clipboard unavailable"));
}

function isSafeMarkdownLink(href: string) {
  return (
    href.startsWith("/api/") ||
    href.startsWith("https://") ||
    href.startsWith("http://") ||
    href.startsWith("mailto:") ||
    href.startsWith("#")
  );
}

function isPreviewableMarkdownImage(src: string) {
  return (
    src.startsWith("/api/") ||
    src.startsWith("https://") ||
    src.startsWith("http://") ||
    src.startsWith("blob:") ||
    src.startsWith("data:image/")
  );
}

const cadenceUrlTransform: UrlTransform = (url, key) => {
  if (key === "href") return isSafeMarkdownLink(url) ? url : null;
  if (key === "src") return isPreviewableMarkdownImage(url) ? url : null;
  return null;
};

function SafeMarkdownLink({ href, children, node: _node, ...props }: ComponentPropsWithoutRef<"a"> & { node?: unknown }) {
  if (!href || !isSafeMarkdownLink(href)) return <span className="blocked-link">{children}</span>;
  const external = href.startsWith("http://") || href.startsWith("https://");
  return <a {...props} href={href} rel={external ? "noreferrer noopener" : undefined} target={external ? "_blank" : undefined}>{children}</a>;
}

function SafeMarkdownImage({ src, alt, node: _node, ...props }: ComponentPropsWithoutRef<"img"> & { node?: unknown }) {
  if (!src || !isPreviewableMarkdownImage(src)) return <span className="blocked-image">图像路径不可直接预览{alt ? `：${alt}` : ""}</span>;
  return <img {...props} src={src} alt={alt ?? ""} loading="lazy" />;
}

function ScrollableMarkdownTable({ children, node: _node, ...props }: ComponentPropsWithoutRef<"table"> & { node?: unknown }) {
  return (
    <div className="timeline-result-table-wrap" role="region" aria-label="结果表格，可横向滚动" tabIndex={0}>
      <table {...props}>{children}</table>
    </div>
  );
}

function artifactDisplayName(artifact: CadenceArtifact): string {
  const name = artifact.name.trim();
  const kind = artifact.kind?.trim().toLowerCase();
  if (kind === "literature_evidence_bundle") return "文献证据包";
  if (/^assistant-[a-f0-9]{8,}\.md$/iu.test(name)) return "Agent 分析报告";
  if (name === "research-state.json") return "研究状态记录";
  if (name === "biostat_request.json") return "生物统计分析请求";
  return name;
}

// Open Science throttles Markdown parsing while the source text grows. Cadence
// receives the same effect from durable/public SSE projections, not from a
// second client-side stream.
function useThrottledValue(value: string, wait = 90) {
  const [shown, setShown] = useState(value);
  const latestRef = useRef(value);
  useEffect(() => {
    latestRef.current = value;
    const timer = window.setTimeout(() => setShown(latestRef.current), wait);
    return () => window.clearTimeout(timer);
  }, [value, wait]);
  return shown;
}

// Open Science's UserMessage shape: the ProjectTurn input hugs its content and
// attachments stay within the same user-authored block.
const UserMessage = memo(function UserMessage({
  turn,
  continuationAgentName,
}: {
  turn: CadenceTurn;
  continuationAgentName?: string;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await copyText(turn.input || "基于附件的研究请求");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      // Clipboard availability is an ambient browser capability. Keeping the
      // message intact is preferable to surfacing a second transient notice.
    }
  };

  return (
    <section className="open-science-user-message">
      <div className="open-science-user-bubble">
        <p>{turn.input || "基于附件的研究请求"}</p>
        {turn.intent?.kind === "continue_research" && (
          <span className="open-science-turn-intent" data-testid={`timeline-intent-${turn.id}`}>
            继续 · {continuationAgentName ?? turn.intent.targetAgentId}
          </span>
        )}
        {turn.attachments.length > 0 && (
          <div className="open-science-message-attachments" aria-label="请求附件">
            {turn.attachments.map((attachment, index) => (
              <span key={`${attachment.id}-${index}`} title={attachment.name}>
                <FileText size={13} aria-hidden="true" />
                {attachment.name}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="open-science-message-meta">
        {turn.createdAt && <time>{formatTime(turn.createdAt)}</time>}
        <button type="button" onClick={() => void copy()} title={copied ? "已复制" : "复制请求"} aria-label="复制请求">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </section>
  );
});

// Follow-ups are server-projected on the parent receipt, so rendering them as
// user messages preserves the thread's true shape after reload. They are not
// synthetic activity and never imply that the Lead accepted or completed them.
const SteeredUserMessage = memo(function SteeredUserMessage({
  turn,
  followUp,
}: {
  turn: CadenceTurn;
  followUp: NonNullable<CadenceTurn["steeredInputs"]>[number];
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await copyText(followUp.input);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      // The durable ProjectTurn remains the source of truth if local copy fails.
    }
  };
  const delivery =
    followUp.deliveryStatus === "delivered"
      ? { label: "已送达当前 Lead", detail: undefined }
      : followUp.deliveryStatus === "rejected"
        ? {
            label: "未送达当前 Lead",
            detail:
              followUp.deliveryError ?? "当前 Lead 未接收这条追加指令。",
          }
        : { label: "正在发送给当前 Lead", detail: undefined };

  return (
    <section
      className="open-science-user-message open-science-steered-user-message"
      data-testid={`timeline-steered-input-${turn.id}-${followUp.followUpId}`}
    >
      <div className="open-science-user-bubble">
        <p>{followUp.input}</p>
        <span
          className={`open-science-turn-intent open-science-steer-delivery is-${followUp.deliveryStatus}`}
          data-testid={`timeline-steered-input-status-${turn.id}-${followUp.followUpId}`}
        >
          {delivery.label}
        </span>
        {delivery.detail && (
          <p className="open-science-steer-delivery-error" role="status">
            {delivery.detail}
          </p>
        )}
      </div>
      <div className="open-science-message-meta">
        <time>{formatTime(followUp.createdAt)}</time>
        <button
          type="button"
          onClick={() => void copy()}
          title={copied ? "已复制" : "复制追加指令"}
          aria-label="复制追加指令"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </section>
  );
});

// Open Science's AgentMessage source intentionally renders as document content
// rather than another large bubble. This keeps the research conclusion primary.
const AgentMessage = memo(function AgentMessage({
  markdown,
  agentId,
  agentName = "Cadence",
  agentRole,
  status,
  sourceLabel,
  sourceTitle,
  turn,
}: {
  markdown: string;
  agentId?: string;
  agentName?: string;
  agentRole?: string;
  status?: string;
  sourceLabel?: string;
  sourceTitle?: string;
  turn: CadenceTurn;
}) {
  const [copied, setCopied] = useState(false);
  const shown = useThrottledValue(markdown);
  const copy = async () => {
    try {
      await copyText(markdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      // See UserMessage: a failed local clipboard write should not alter the
      // server-owned research record.
    }
  };
  return (
    <section
      className="open-science-agent-message"
      data-status={turn.status}
      style={
        {
          "--open-science-agent-accent": agentReplyAccent(agentId),
        } as CSSProperties
      }
    >
      <header>
        <div className="open-science-agent-identity">
          <span className="open-science-agent-avatar" aria-hidden="true">
            {agentId ? agentReplyInitial(agentName) : <FlaskConical size={15} strokeWidth={1.8} />}
          </span>
          <div>
            <strong>{agentName}</strong>
            {agentRole && <small>{agentRole}</small>}
          </div>
        </div>
        <div className="open-science-agent-meta">
          {status && <span data-status={turn.status}>{status}</span>}
          {sourceLabel && <small title={sourceTitle}>{sourceLabel}</small>}
        </div>
      </header>
      <div className="open-science-agent-response">
        {/*
          Streamdown retains its parser output for append-only streams. A
          ProjectTurn refresh can instead replace a public result after replay
          or correction, so remount at the throttled content boundary rather
          than leaving a stale scientific statement visible.
        */}
        <Streamdown
          key={`${turn.id}:${shown}`}
          className="open-science-agent-markdown"
          components={{ a: SafeMarkdownLink, img: SafeMarkdownImage, table: ScrollableMarkdownTable }}
          controls={false}
          isAnimating={!turn.submissionUncertain && (turn.status === "pending" || turn.status === "running")}
          mode="streaming"
          parseIncompleteMarkdown
          plugins={{ cjk, math }}
          rehypePlugins={[]}
          remarkRehypeOptions={{ allowDangerousHtml: false }}
          skipHtml
          urlTransform={cadenceUrlTransform}
        >
          {shown}
        </Streamdown>
      </div>
      <div className="open-science-message-actions">
        <button type="button" onClick={() => void copy()} title={copied ? "已复制" : "复制回复"} aria-label="复制回复">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </section>
  );
});

function EmptyAgentMessage({ turn, message }: { turn: CadenceTurn; message: string }) {
  return (
    <section className="open-science-agent-message open-science-agent-pending">
      <header>
        <strong>Cadence</strong>
        <span data-status={turn.status}>{statusLabel(turn)}</span>
      </header>
      <p className="open-science-agent-fallback">{message}</p>
      {turn.error && <p className="open-science-agent-error" role="alert">{turn.error}</p>}
    </section>
  );
}

function RequestStatusMessage({ turn, message }: { turn: CadenceTurn; message: string }) {
  return (
    <section
      className="open-science-agent-message open-science-request-status"
      data-testid={`timeline-request-status-${turn.id}`}
      role="status"
    >
      <header>
        <strong>请求状态</strong>
        <span data-status={turn.status}>{statusLabel(turn)}</span>
      </header>
      <p className="open-science-agent-fallback">{message}</p>
      {turn.error && <p className="open-science-agent-error" role="alert">{turn.error}</p>}
    </section>
  );
}

// The source BlockList keeps activity groups in the same flat stream as user
// and agent messages. Cadence's existing feed retains ToolGroup's inner
// disclosure and its agent/submission fence.
function ActivityBlock({
  turn,
  events,
  agents,
  running,
  cancelling,
  cancelError,
  onCancel,
}: {
  turn: CadenceTurn;
  events: CadenceAgentEvent[];
  agents: CadenceAgent[];
  running: boolean;
  cancelling: boolean;
  cancelError?: string;
  onCancel?: () => void;
}) {
  return (
    <section className="open-science-activity-block" data-testid="open-science-activity-block">
      <ConversationActivityFeed agents={agents} events={events} projectId={turn.projectId ?? ""} running={running} />
      {onCancel && running && (
        <button className="open-science-cancel" type="button" disabled={cancelling} onClick={onCancel}>
          {cancelling ? "取消中…" : "取消当前运行"}
        </button>
      )}
      {cancelError && <p className="open-science-agent-error" role="alert">{cancelError}</p>}
    </section>
  );
}

function ReplyList({
  replies,
  turn,
}: {
  replies: OpenSciencePublicAgentReply[];
  turn: CadenceTurn;
}) {
  if (!replies.length) return null;
  return (
    <section className="open-science-reply-list">
      <div id={`timeline-agent-replies-${turn.id}`} data-testid={`timeline-agent-replies-${turn.id}`}>
        {replies.map((reply) => (
          <AgentMessage
            agentId={reply.agentId}
            agentName={reply.agentName}
            agentRole={reply.agentRole}
            key={reply.id}
            markdown={reply.text}
            sourceLabel={reply.tsMs ? formatTime(reply.tsMs) : undefined}
            sourceTitle={reply.sourceLabel}
            turn={turn}
          />
        ))}
      </div>
    </section>
  );
}

// Artifact is a stable scientific object, not a Markdown-derived attachment.
// Keep its source-level Open Science block in the thread, keyed by Cadence's
// existing immutable Artifact ID.
function artifactIcon(artifact: CadenceArtifact) {
  const identity = `${artifact.type} ${artifact.kind ?? ""}`.toLocaleLowerCase();
  if (identity.includes("notebook")) return <NotebookPen size={18} aria-hidden="true" />;
  if (identity.includes("csv") || identity.includes("table") || identity.includes("data")) {
    return <FileSpreadsheet size={18} aria-hidden="true" />;
  }
  if (identity.includes("image")) return <ImageIcon size={18} aria-hidden="true" />;
  if (identity.includes("structure")) return <Box size={18} aria-hidden="true" />;
  if (identity.includes("chart") || identity.includes("curve") || identity.includes("flow")) {
    return <ChartNoAxesCombined size={18} aria-hidden="true" />;
  }
  if (identity.includes("html") || identity.includes("code")) {
    return <FileCode2 size={18} aria-hidden="true" />;
  }
  return <FileText size={18} aria-hidden="true" />;
}

function artifactSupportingText(artifact: CadenceArtifact) {
  const description = artifact.description?.trim();
  if (description) return description;
  return [artifact.type, artifact.updatedAt].filter(Boolean).join(" · ");
}

function ArtifactLinks({
  artifacts,
  turn,
  onOpenArtifact,
}: {
  artifacts: CadenceArtifact[];
  turn: CadenceTurn;
  onOpenArtifact(artifactId: string): void;
}) {
  if (!artifacts.length) return null;
  return (
    <section className="open-science-artifact-links" aria-label="本次请求关联的 Artifact">
      {artifacts.map((artifact) => (
        <button
          aria-label={`在右侧检查器中打开 ${artifactDisplayName(artifact)}`}
          data-testid={`timeline-artifact-open-${turn.id}-${artifact.id}`}
          key={artifact.id}
          type="button"
          onClick={() => onOpenArtifact(artifact.id)}
        >
          <span className="open-science-artifact-icon">{artifactIcon(artifact)}</span>
          <span className="open-science-artifact-copy">
            <strong>{artifactDisplayName(artifact)}</strong>
            <small>{artifactSupportingText(artifact)}</small>
          </span>
          <span className="open-science-artifact-action" aria-hidden="true">
            <small>{artifact.type}</small>
            <span>打开</span>
            <PanelRightOpen size={15} />
          </span>
        </button>
      ))}
    </section>
  );
}

function hasVisibleActivity(events: CadenceAgentEvent[]) {
  return events.some(({ event }) => {
    if (
      [
        "agent_reasoning",
        "agent_reasoning_delta",
        "reasoning_content_delta",
        "plan_update",
        "plan_delta",
        "mcp_tool_call_begin",
        "mcp_tool_call_end",
        "web_search_begin",
        "web_search_end",
        "exec_command_begin",
        "exec_command_output_delta",
        "exec_command_end",
      ].includes(event.type)
    ) {
      return true;
    }
    return /^(collab_(?:agent_spawn|agent_interaction|waiting))_(?:begin|end)$/u.test(
      event.type,
    );
  });
}

export function OpenScienceConversationTurn({
  projectId,
  turn,
  agents,
  events,
  artifacts,
  active,
  cancellingTurn,
  cancelError,
  onCancelActive,
  onOpenArtifact,
  onContinueResearch,
  selectedAgentId,
  publicReplies,
  requestStatusMessage,
  resultSections,
  responseFallback,
}: {
  projectId: string;
  turn: CadenceTurn;
  agents: CadenceAgent[];
  events: CadenceAgentEvent[];
  artifacts: CadenceArtifact[];
  active: boolean;
  cancellingTurn: boolean;
  cancelError?: string;
  onCancelActive?: () => void;
  onOpenArtifact(artifactId: string): void;
  onContinueResearch?(turn: CadenceTurn, agentId: string): void;
  selectedAgentId?: string;
  publicReplies: OpenSciencePublicAgentReply[];
  requestStatusMessage?: string;
  resultSections: string[];
  responseFallback: string;
}) {
  const running = !turn.submissionUncertain && (turn.status === "pending" || turn.status === "running");
  const activityIds = new Set(turn.activityIds ?? []);
  const scopedEvents = events.filter((event) => activityIds.has(event.submissionId));
  const scopedAgents = agents.filter((agent) => (turn.routedAgentIds ?? []).includes(agent.id));
  const hasProgress = (turn.routedAgentIds?.length ?? 0) > 0 || activityIds.size > 0 || (turn.artifactIds?.length ?? 0) > 0;
  const showActivity = hasVisibleActivity(scopedEvents);
  const continuationTargetId =
    turn.intent?.kind === "continue_research"
      ? turn.intent.targetAgentId
      : undefined;
  const continuationAgent = continuationTargetId
    ? agents.find((agent) => agent.id === continuationTargetId)
    : undefined;
  const selectedAgent = selectedAgentId
    ? agents.find((agent) => agent.id === selectedAgentId)
    : undefined;
  const canContinue = Boolean(
    selectedAgent &&
      onContinueResearch &&
      turn.requestId &&
      !turn.submissionUncertain &&
      activityIds.size > 0 &&
      (turn.routedAgentIds ?? []).includes(selectedAgent.id),
  );
  const timestamp = (value: string | number) => typeof value === "number" ? value : Date.parse(value);
  const duration = turn.createdAt && turn.completedAt
    ? timestamp(turn.completedAt) - timestamp(turn.createdAt)
    : undefined;

  return (
    <article
      className={`open-science-turn timeline-turn timeline-turn-${turn.status}`}
      data-testid={`timeline-turn-${turn.id}`}
      data-status={turn.status}
      id={`project-turn-${turn.id}`}
      tabIndex={-1}
    >
      <UserMessage turn={turn} continuationAgentName={continuationAgent?.name} />
      {(turn.steeredInputs ?? []).map((followUp) => (
        <SteeredUserMessage
          followUp={followUp}
          key={followUp.followUpId}
          turn={turn}
        />
      ))}
      {showActivity && (
        <ActivityBlock
          agents={scopedAgents}
          cancelError={cancelError}
          cancelling={cancellingTurn}
          events={scopedEvents}
          onCancel={active && running ? onCancelActive : undefined}
          running={running}
          turn={{ ...turn, projectId }}
        />
      )}
      <ReplyList replies={publicReplies} turn={turn} />
      <ArtifactLinks artifacts={artifacts} onOpenArtifact={onOpenArtifact} turn={turn} />
      {requestStatusMessage && (
        <RequestStatusMessage message={requestStatusMessage} turn={turn} />
      )}
      {resultSections.length > 0 ? (
        <div className="open-science-result-stack">
          {resultSections.map((content, index) => (
            <AgentMessage
              key={`${turn.id}-result-${index}`}
              markdown={content}
              status={statusLabel(turn)}
              turn={turn}
            />
          ))}
        </div>
      ) : !requestStatusMessage ? (
        <EmptyAgentMessage message={responseFallback} turn={turn} />
      ) : null}
      {turn.error && resultSections.length > 0 && <p className="open-science-agent-error" role="alert">{turn.error}</p>}
      <footer className="open-science-turn-footer">
        {duration !== undefined && Number.isFinite(duration) && duration >= 0 && <span>服务端耗时 {formatElapsed(duration)}</span>}
        {turn.evidenceIds?.length ? <span>证据 {turn.evidenceIds.length}</span> : null}
        {canContinue && selectedAgent && (
          <button type="button" onClick={() => onContinueResearch?.(turn, selectedAgent.id)}>
            继续由当前专家研究
          </button>
        )}
      </footer>
      {active && running && <span className="visually-hidden" role="status" aria-live="polite">{statusLabel(turn)}</span>}
      {active && running && !hasProgress && <p className="open-science-no-activity timeline-no-activity">请求已提交，正在等待系统分派；当前尚无可归因的 Agent 活动或 Artifact。</p>}
    </article>
  );
}
